import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  GrupoSistema,
  GrupoSistemaPOST,
  GruposSistemaPaginatedRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchGrupoSistemas = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetGrupoSistemasParams>) => {
  return useQuery({
    queryKey: ['grupo-sistemas', ...Object.values(params || {})],
    queryFn: () => getGrupoSistemas(params),
    enabled: enabled,
  });
};

export const useGetSystemPermissionsByGroupID = (id: number) => {
  return useQuery({
    queryKey: ['grupo-sistema-permisos', id],
    queryFn: () => getSystemPermissionsByGroupID(id),
    retry: false,
  });
};


export const useCreateSystemGroupAndPermissions = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createSystemGroupAndPermissions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupo-sistemas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo de Sistema creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateSystemGroupPermissions = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateSystemGroupPermissions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupo-sistemas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo de Sistema actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteGrupoSistema = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deleteGrupoSistema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupo-sistemas'] });
      setAlert(
        true,
        'Grupo de Sistema eliminado correctamente',
        AlertSeverityEnum.success
      );
    },
    onError: err => {
      setAlert(
        true,
        err?.message || 'Error al eliminar el Grupo de Sistema',
        AlertSeverityEnum.error
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetGrupoSistemasParams = {
  page?: number;
  page_size?: number;

  // filters
  name?: string;
};
export type CreateGrupoSistemaParams = GrupoSistemaPOST;
export type UpdateGrupoSistemaParams = {
  id: number;
  data: CreateGrupoSistemaParams;
};
export type CreateSystemGroupAndPermissionsParams = GrupoSistemaPOST;

export const getGrupoSistemas = async (params?: GetGrupoSistemasParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  return get<GruposSistemaPaginatedRes>(
    `${urlBackend}api/v1/get-grupos/?${queryParams}`,
    true
  );
};

// permissions
export const getSystemPermissionsByGroupID = async (
  id: number
): Promise<GrupoSistema | undefined> => {
  try {
    const { data } = await get<GrupoSistema>(
      `${urlBackend}api/v1/group/${id}/`,
      true
    );
    return data as any;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createSystemGroupAndPermissions = async ( data: CreateSystemGroupAndPermissionsParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<GrupoSistema>(`${urlBackend}api/v1/crear-grupo/`, data, true);
};

export const deleteGrupoSistema = async (id: number) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return remove<GrupoSistema>(`${urlBackend}api/v1/group/${id}/`, true);
};

type UpdateSystemGroupPermissionsParams = {
  id: number;
  data: GrupoSistemaPOST;
};
export const updateSystemGroupPermissions = async ({
  id,
  data,
}: UpdateSystemGroupPermissionsParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put(`${urlBackend}api/v1/group/${id}/`, data, true);
};
